import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Boxes, Search, ExternalLink, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (n, d = 0) =>
  Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });

/** Bir cariye bağlı tüm stokları listeler.
 * Backend filtresi: GET /stocks?contact_id=... */
export default function ContactStocksModal({ contact, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stocks?contact_id=${contact.id}`);
      setRows(data || []);
    } catch (e) {
      toast.error("Stoklar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [contact.id]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLocaleLowerCase("tr");
    return rows.filter((it) =>
      (it.name || "").toLocaleLowerCase("tr").includes(s) ||
      (it.sku || "").toLocaleLowerCase("tr").includes(s)
    );
  }, [rows, q]);

  const totals = useMemo(() => {
    let onHand = 0, critical = 0;
    rows.forEach((it) => {
      onHand += Number(it.qty_on_hand) || 0;
      if (it.min_stock != null && Number(it.qty_on_hand) <= Number(it.min_stock)) critical += 1;
    });
    return { onHand, critical };
  }, [rows]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="contact-stocks-modal"
           className="bg-white border-2 border-black w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Boxes size={16} /> Bu Cariye Bağlı Stoklar
            </h2>
            <div className="text-xs text-neutral-500 mt-0.5">{contact.name}</div>
          </div>
          <button onClick={onClose} className="p-1 text-xl leading-none" data-testid="cs-close">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Özet kutuları */}
          <div className="grid grid-cols-3 gap-3">
            <SummaryBox label="Ürün Sayısı" value={fmt(rows.length, 0)} tid="cs-summary-count" tone="black" />
            <SummaryBox label="Toplam Mevcut" value={fmt(totals.onHand, 0)} tid="cs-summary-onhand" tone="black" />
            <SummaryBox label="Kritik Kalem" value={fmt(totals.critical, 0)} tid="cs-summary-critical"
                        tone={totals.critical > 0 ? "rose" : "black"} />
          </div>

          {/* Arama */}
          <div className="relative max-w-md">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="cs-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="SKU veya ad ara…"
              className="w-full border border-black/20 pl-8 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          {/* Tablo */}
          <div className="brutal-card overflow-hidden">
            <table className="w-full text-sm" data-testid="cs-table">
              <thead>
                <tr className="border-b-2 border-black/80 bg-neutral-50">
                  <th className="text-left px-3 py-2 label-caps">SKU</th>
                  <th className="text-left px-3 py-2 label-caps">Ad</th>
                  <th className="text-right px-3 py-2 label-caps">Ölçü (mm)</th>
                  <th className="text-left px-3 py-2 label-caps">Karton</th>
                  <th className="text-right px-3 py-2 label-caps">Mevcut</th>
                  <th className="text-right px-3 py-2 label-caps">Min</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-8 text-neutral-500 text-sm">Yükleniyor…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-neutral-500 text-sm">
                      {q ? "Arama sonucu yok." : (
                        <>
                          Bu cariye bağlı stok yok.{" "}
                          <Link to="/stok" onClick={onClose} className="underline decoration-black underline-offset-4" data-testid="cs-empty-link">
                            Stok kartında müşteri olarak ekleyebilirsiniz →
                          </Link>
                        </>
                      )}
                    </td>
                  </tr>
                ) : filtered.map((it) => {
                  const q_ = Number(it.qty_on_hand) || 0;
                  const low = it.min_stock != null && q_ <= Number(it.min_stock);
                  return (
                    <tr key={it.id} className="border-b border-black/5 hover:bg-neutral-50" data-testid={`cs-row-${it.id}`}>
                      <td className="px-3 py-2 font-mono-num text-xs font-semibold">{it.sku}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{it.name}</div>
                        {it.description && (
                          <div className="text-[11px] text-neutral-500 truncate max-w-xs">{it.description}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num text-xs">
                        {it.length_mm}×{it.width_mm}×{it.height_mm}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {(it.flute || it.quality) ? (
                          <>
                            <span className="font-semibold">{it.flute || "—"}</span>
                            {it.quality && <> · {it.quality}</>}
                            {it.grammage != null && <span className="text-neutral-500"> · {fmt(it.grammage, 0)}g</span>}
                          </>
                        ) : <span className="text-neutral-400">—</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono-num ${low ? "text-rose-700 font-bold" : ""}`}>
                        {fmt(q_, 0)} <span className="text-[10px] text-neutral-500">{it.unit}</span>
                        {low && (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest bg-rose-100 border border-rose-400 px-1 py-0.5"
                                title="Minimum stok seviyesinin altında">
                            <AlertTriangle size={9} /> Kritik
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num text-xs text-neutral-600">
                        {it.min_stock != null ? fmt(it.min_stock, 0) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Link to="/stok" onClick={onClose} data-testid={`cs-open-${it.id}`}
                              className="p-1.5 inline-flex hover:bg-neutral-100" title="Stok sayfasında aç">
                          <ExternalLink size={13} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryBox({ label, value, tid, tone }) {
  const toneCls = {
    rose: "border-rose-600 text-rose-700",
    black: "border-black text-black",
  }[tone] || "border-black text-black";
  return (
    <div className={`border-2 ${toneCls} p-3`} data-testid={tid}>
      <div className="label-caps text-[10px]">{label}</div>
      <div className="font-mono-num text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}
